import type { Writable } from "svelte/store";
import type { FileluConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// The part of FileLu that takes paths does not answer other origins. Calls go
// through /api/filelu, which talks to https://filelu.com/rclone. The API key
// is a request header.

export const prop_filelu_code = "filelu_code";

const noRenameMsg = "FileLu cannot rename or move";

class FileSystemFilelu extends FS {
  constructor(config: FileluConfig) {
    super(config);
  }

  get apiKey(): string {
    return (this.config as FileluConfig).apiKey;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await fileluCall(this.apiKey, mkURL("/api/filelu/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("filelu list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  // A link that works for a while without the API key.
  async getPublicURL(path: string): Promise<string> {
    let js = await fileluCall(this.apiKey, mkURL("/api/filelu/link", "path", path));
    throwIf(typeof js.url != "string" || js.url == "", "filelu gave no link");
    return js.url;
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/filelu/download", "path", path);
    let xhr = await fileluSend("GET", url, this.apiKey, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/filelu/upload", "path", path);
    await fileluSend("PUT", url, this.apiKey, file, httpProgress);
  }

  // The API has no call for it.
  async rename(path: string, newPath: string): Promise<void> {
    throw new Error(noRenameMsg);
  }

  async createDirectory(dirPath: string): Promise<void> {
    await fileluCall(this.apiKey, mkURL("/api/filelu/mkdir", "path", dirPath), "POST");
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await fileluCall(this.apiKey, mkURL("/api/filelu/delete", "path", path), "POST");
  }
}

export async function testFileluConnection(apiKey: string): Promise<void> {
  await fileluCall(apiKey, "/api/filelu/test");
}

async function fileluCall(apiKey: string, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: { "X-Filelu-Key": apiKey },
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `filelu ${rsp.status}`);
  }
  return js;
}

function fileluSend(
  method: string,
  url: string,
  apiKey: string,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    xhr.setRequestHeader("X-Filelu-Key", apiKey);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      fileluErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`filelu ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("filelu request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function fileluErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `filelu ${xhr.status}`;
}

export function makeFileSystemFilelu(config: FileluConfig): FileSystemFilelu {
  return new FileSystemFilelu(config);
}
